import { useConfigStore } from "../Stores/Config";
import { useTicketStore } from "../Stores/Ticket";

/**
 * Chat cards: interactive cards Kai shows under its reply (`data.widget` on
 * the BOT comment, built by the server's show_widget tool and the billing /
 * commerce / booking tools). The server validates and bounds every field
 * (https-only links and images, preformatted money and dates); this module
 * only reads them defensively and sends the customer's answer back.
 *
 * The 15 types and their props are the contract in
 * design-handoffs/chat-cards/SPEC.md §2.
 */
export const CHAT_WIDGET_TYPES = [
  "quick_replies",
  "show_me",
  "form",
  "confirm",
  "status",
  "booking",
  "subscription",
  "invoices",
  "payment_issue",
  "plan_compare",
  "products",
  "order",
  "return",
  "bag",
  "offer",
  "feature_requests",
] as const;
export type ChatWidgetType = (typeof CHAT_WIDGET_TYPES)[number];

export type ChatWidgetTone =
  "neutral" | "info" | "success" | "warning" | "danger";
export type ChatWidgetAction = "confirm" | "cancel" | "submit" | "select";
export type ChatWidgetValues = Record<string, string | string[]>;

export interface ChatWidgetStatus {
  label: string;
  tone?: ChatWidgetTone;
}

export interface ChatWidgetInput {
  name: string;
  label: string;
  type: "text" | "email" | "number" | "date" | "textarea" | "select" | "chips";
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

export interface ChatWidgetResponse {
  action: ChatWidgetAction;
  values: ChatWidgetValues;
  /** What the customer "said" (their message in the thread). */
  label: string;
  at?: string;
  /** confirm: the customer took it back with Undo (server keeps action `confirm`). */
  undoneAt?: string;
  /** Billing / commerce confirm: the stored action had expired; nothing ran. */
  expired?: boolean;
  /**
   * Client-only: sent, but the server hasn't confirmed it yet. Cards whose
   * answer runs something on the server (billing, commerce) show it as
   * pending until `comment.updated` replaces the widget.
   */
  pending?: boolean;
}

// Server contract: Server-widget-kit/src/services/widgets/widget.types.ts and
// design-handoffs/chat-cards/PAYLOAD-CHANGES.md. Empty optional props
// (arrays, `false`, unset strings) are omitted, so every list is read with a
// fallback.
interface ChatWidgetBase {
  id: string;
  title?: string;
  subtitle?: string;
  response?: ChatWidgetResponse;
}

export interface QuickRepliesWidget extends ChatWidgetBase {
  type: "quick_replies";
  options: { id: string; label: string }[];
}

export interface ShowMeWidget extends ChatWidgetBase {
  type: "show_me";
  url: string;
  title: string;
  description?: string;
  breadcrumb?: string;
  target?: string;
  preview?: string;
}

export interface FormWidget extends ChatWidgetBase {
  type: "form";
  inputs: ChatWidgetInput[];
  submitLabel?: string;
}

export interface ConfirmWidget extends ChatWidgetBase {
  type: "confirm";
  description?: string;
  fields?: { label: string; value: string }[];
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  icon?: "alert" | "info";
  /** Receipt line once confirmed ("Cancellation scheduled · Pro ends 14 Mar 2027"). */
  receiptLabel?: string;
  /** Ghost button on the receipt that takes it back (sends `cancel`). */
  undoLabel?: string;
  /** The stored billing / commerce action the answer runs on the server. */
  billingActionId?: string;
  commerceActionId?: string;
  /** `expired`: the stored action expired before the customer answered. */
  state?: string;
}

export interface StatusWidget extends ChatWidgetBase {
  type: "status";
  status: ChatWidgetStatus;
  steps: {
    label: string;
    state: "done" | "current" | "upcoming" | "failed";
    date?: string;
    detail?: string;
  }[];
  eyebrow?: string;
  footnote?: string;
}

export interface BookingDay {
  date: string;
  label: string;
  dayNumber: string;
  slots: { start: string; label: string }[];
}

/** A required booking question the card asks with the pick (`values[name]`). */
export interface BookingField {
  name: string;
  label: string;
  type: "phone" | "text" | "textarea";
}

/** Server-written outcome of the pick (never from Kai). */
export interface BookingResult {
  /** `requested`: the host still has to confirm it (Cal.com "requires confirmation"). */
  status: "booking" | "booked" | "requested" | "confirm_in_provider" | "failed";
  start?: string;
  /** booked: "Wed 15 Oct, 11:30" */
  label?: string;
  /** booked: where the invite went. */
  email?: string;
  /** confirm_in_provider: the pre-filled Calendly page; failed (no_times / needs_booking_page): the booking page. */
  url?: string;
  /** failed: why, in English (the Messenger prefers its string for `reason`). */
  message?: string;
  /** requested: who has to confirm. */
  host?: string;
  reason?:
    | "slot_unavailable"
    | "no_times"
    | "provider_error"
    | "email_missing"
    | "not_available"
    | "rate_limited"
    | "fields_required"
    | "needs_booking_page"
    | "not_matched";
  at?: string;
}

export interface BookingWidget extends ChatWidgetBase {
  type: "booking";
  host: { name: string; role?: string; avatar?: string };
  durationLabel?: string;
  timezoneLabel?: string;
  days: BookingDay[];
  provider?: "calendly" | "calcom";
  /** The provider's public booking page ("More times"). */
  bookingUrl?: string;
  /** Who gets the invite. */
  invitee?: { name?: string; email?: string };
  /** Required questions to answer with the pick. */
  fields?: BookingField[];
  /** IANA zone the server labelled `days` in. */
  timeZone?: string;
  booking?: BookingResult;
}

export interface SubscriptionWidget extends ChatWidgetBase {
  type: "subscription";
  status?: ChatWidgetStatus;
  price: string;
  interval?: string;
  renewsOn?: string;
  endsOn?: string;
  meters?: { label: string; used: number; limit: number; unit?: string }[];
  url?: string;
  urlLabel?: string;
}

export interface InvoicesWidget extends ChatWidgetBase {
  type: "invoices";
  items: {
    id: string;
    number: string;
    date: string;
    amount: string;
    status?: ChatWidgetStatus;
    url?: string;
  }[];
  url?: string;
}

export interface PaymentIssueWidget extends ChatWidgetBase {
  type: "payment_issue";
  amount: string;
  date?: string;
  method?: string;
  reason?: string;
  updateUrl?: string;
  canRetry?: boolean;
}

export interface PlanCompareWidget extends ChatWidgetBase {
  type: "plan_compare";
  plans: {
    id: string;
    name: string;
    price: string;
    interval?: string;
    features: string[];
    current?: boolean;
    recommended?: boolean;
  }[];
  ctaLabel?: string;
  footnote?: string;
}

export interface ProductItem {
  id: string;
  title: string;
  subtitle?: string;
  image?: string;
  price?: string;
  compareAtPrice?: string;
  rating?: number;
  reviewCount?: number;
  badge?: string;
  url?: string;
  addToCartUrl?: string;
  swatches?: { label: string; color: string }[];
}

export interface ProductsWidget extends ChatWidgetBase {
  type: "products";
  items: ProductItem[];
}

export interface OrderWidget extends ChatWidgetBase {
  type: "order";
  orderNumber: string;
  status?: ChatWidgetStatus;
  headline: string;
  subline?: string;
  progress?: { steps: string[]; current: number; partial?: boolean };
  items?: { title: string; image?: string }[];
  carrier?: string;
  trackingUrl?: string;
}

export interface ReturnWidget extends ChatWidgetBase {
  type: "return";
  orderNumber: string;
  /** `quantity`: what the line holds; above 1 the card shows a stepper (1…quantity). */
  items: {
    id: string;
    title: string;
    subtitle?: string;
    image?: string;
    quantity?: number;
  }[];
  reasons: string[];
  policyNote?: string;
  allowExchange?: boolean;
  /** What the customer already told Kai, ticked / picked when the card opens. */
  selected?: { id: string; quantity: number }[];
  reason?: string;
  /** Ask the team: "Request return", and the receipt says "Sent to the team". */
  teamReviews?: boolean;
}

export interface BagWidget extends ChatWidgetBase {
  type: "bag";
  items: {
    title: string;
    subtitle?: string;
    image?: string;
    price: string;
    qty: number;
  }[];
  subtotal: string;
  discounts?: { label: string; amount: string }[];
  delivery?: string;
  total: string;
  checkoutUrl?: string;
}

export interface OfferWidget extends ChatWidgetBase {
  type: "offer";
  headline: string;
  code: string;
  expires?: string;
  applyUrl?: string;
}

export type FeatureRequestsState =
  | "choose"
  | "upvoted"
  | "dismissed"
  | "proposed"
  | "created"
  | "recorded"
  | "error";

export interface FeatureRequestItem {
  id: string;
  shareToken?: string;
  title: string;
  statusLabel?: string;
  statusTone?: ChatWidgetTone;
  upvotesCount?: number;
  /** The customer already voted for it. */
  upvoted?: boolean;
  /** The public portal page (only for public items). */
  url?: string;
}

/**
 * Kai intake, feature requests (design-handoffs/kai-intake/PLAN.md §7): up to
 * three public requests that match what the customer asked for, a proposal
 * for a new one when none fits, and what came of it. The server builds it
 * (the model never writes items) and moves `state`; the card only answers.
 */
export interface FeatureRequestsWidget extends ChatWidgetBase {
  type: "feature_requests";
  state?: FeatureRequestsState;
  query?: string;
  items?: FeatureRequestItem[];
  /** upvoted: the request the customer voted for. */
  selectedId?: string;
  /** proposed: what Kai would file. */
  proposal?: { title: string; description?: string };
  /** created: the new request. */
  created?: { id?: string; title: string; statusLabel?: string; url?: string };
  /** Anonymous session: the card asks for an email before a vote or a new request. */
  emailRequired?: boolean;
  /** error / recorded: English fallback text from the server. */
  message?: string;
}

export type ChatWidgetData =
  | QuickRepliesWidget
  | ShowMeWidget
  | FormWidget
  | ConfirmWidget
  | StatusWidget
  | BookingWidget
  | SubscriptionWidget
  | InvoicesWidget
  | PaymentIssueWidget
  | PlanCompareWidget
  | ProductsWidget
  | OrderWidget
  | ReturnWidget
  | BagWidget
  | OfferWidget
  | FeatureRequestsWidget;

/** The widget on a comment, or undefined when it is missing or of a type this build can't render. */
export const parseChatWidget = (raw: any): ChatWidgetData | undefined => {
  if (!raw || typeof raw !== "object") return undefined;
  if (
    typeof raw.id !== "string" ||
    !(CHAT_WIDGET_TYPES as readonly string[]).includes(raw.type)
  ) {
    return undefined;
  }
  return raw as ChatWidgetData;
};

/**
 * The words of a card this build can't render (a type added after it
 * shipped): its title, subtitle and description, so a card-only message
 * never disappears. Undefined when there is nothing to show.
 */
export const chatWidgetFallbackText = (raw: any): string | undefined => {
  if (!raw || typeof raw !== "object") return undefined;
  const text = [raw.title, raw.subtitle, raw.description]
    .filter(
      (part): part is string =>
        typeof part === "string" && part.trim().length > 0,
    )
    .join("\n");
  return text.length > 0 ? text : undefined;
};

/** Card types that wait for the customer's answer (the rest only show something). */
const ANSWERABLE_CHAT_WIDGET_TYPES: readonly ChatWidgetType[] = [
  "quick_replies",
  "form",
  "confirm",
  "booking",
  "payment_issue",
  "plan_compare",
  "products",
  "return",
  "feature_requests",
];

/**
 * A card on the comment still waits for the customer (not answered yet):
 * Kai's answer is not done, so it gets no "Was this helpful?" yet.
 */
export const isChatWidgetAwaitingAnswer = (raw: any): boolean => {
  const widget = parseChatWidget(raw);
  if (!widget || widget.response) return false;
  if (!ANSWERABLE_CHAT_WIDGET_TYPES.includes(widget.type)) return false;
  if (widget.type === "feature_requests") {
    return widget.state === "choose" || widget.state === "proposed";
  }
  return true;
};

/** Only absolute https links leave the Messenger (the server already enforces it). */
export const isSafeWidgetUrl = (url?: string): url is string =>
  typeof url === "string" && url.startsWith("https://");

/**
 * Images: https (all the server stores) or an inline raster/SVG data image
 * (mock fixtures). An <img> never runs script, so data images are safe here.
 */
export const isSafeWidgetImage = (src?: string): src is string =>
  typeof src === "string" &&
  (src.startsWith("https://") || src.startsWith("data:image/"));

/** Swatch colours go into a style attribute: plain hex only. */
export const isSafeSwatchColor = (color?: string): color is string =>
  typeof color === "string" && /^#[0-9a-f]{3,8}$/i.test(color);

export const CHAT_WIDGET_STRING_DEFAULTS = {
  widgetConfirm: "Confirm",
  widgetCancel: "Cancel",
  widgetConfirmed: "Confirmed",
  widgetYouConfirmed: "You confirmed",
  widgetNoChanges: "No changes made",
  widgetUndo: "Undo",
  widgetUndone: "Undone",
  widgetJustNow: "just now",
  widgetMinutesAgo: "{n} min ago",
  widgetSubmit: "Send",
  widgetSent: "Sent",
  widgetOpen: "Open",
  widgetShowMe: "Show me",
  widgetView: "View",
  widgetRequired: "Required",
  widgetOptional: "Optional",
  widgetPickTime: "Pick a time",
  widgetBook: "Book {slot}",
  widgetBooked: "Booked",
  widgetBookingPending: "Booking {slot}…",
  widgetInviteGoesTo: "Invite goes to {email}",
  widgetInviteSentTo: "invite sent to {email}",
  widgetMoreTimes: "More times",
  widgetFinishBooking: "Finish booking in {provider}",
  widgetFinishBookingHint: "One more step: confirm {slot} in {provider}.",
  widgetSlotUnavailable:
    "That time is no longer available. Please pick another one.",
  widgetBookingFailed:
    "The booking didn't go through. Please pick a time again.",
  widgetBookingEmailMissing:
    "We need your email to send the invite. Please share it, then pick a time again.",
  widgetBookingRequested: "Requested, waiting for {host} to confirm",
  widgetBookingNotAvailable:
    "Booking isn't available right now, the team has been told.",
  widgetBookingRateLimited:
    "Lots of bookings right now. Please try again in a moment.",
  widgetBookingTemporary:
    "The booking didn't go through. Please try again in a moment.",
  widgetBookingFieldsRequired: "Please fill in the details, then book.",
  widgetBookingNeedsPage:
    "This meeting asks a few more questions. Please finish on the booking page.",
  widgetBookingNotMatched:
    "That booking couldn't be matched to this chat. Please finish it on the {provider} page.",
  widgetNoTimesLeft: "No times left.",
  widgetOpenBookingPage: "Here's the booking page",
  widgetAskTheTeam: "Ask the team here in the chat.",
  widgetTimesIn: "Times in {timezone}",
  widgetTimezone: "Time zone",
  widgetSubscription: "Subscription",
  widgetRenews: "Renews",
  widgetEnds: "Ends",
  widgetMeterOf: "{used} of {limit}",
  widgetManageBilling: "Manage billing",
  widgetInvoices: "Invoices",
  widgetAllInvoices: "All invoices",
  widgetDownload: "Download {name}",
  widgetPaymentFailed: "Payment failed",
  widgetRetryNow: "Retry now",
  widgetRetryRequested: "Retry requested",
  widgetUpdateCard: "Update card",
  widgetCurrent: "Current",
  widgetBestFit: "Best fit",
  widgetSwitchTo: "Switch to {plan}",
  widgetSelected: "Selected",
  widgetAddToBag: "Add {name} to bag (opens your cart)",
  widgetOpenedInCart: "Opened in your cart",
  widgetAskAbout: "Ask about {name}",
  widgetAskAboutThis: "Ask about this",
  widgetAskedAbout: "Asked about this",
  widgetViewItem: "View {name}",
  widgetOrder: "Order {number}",
  widgetTrackPackage: "Track package",
  widgetReturnItems: "Return items",
  widgetReturnItem: "Return {name}",
  widgetReason: "Reason",
  widgetExchange: "Exchange",
  widgetGetReturnLabel: "Get return label",
  widgetRequestReturn: "Request return",
  widgetReturnSentToTeam: "Sent to the team",
  widgetReturnQuantityOf: "{quantity} of {total} × {name}",
  widgetReturnQuantity: "How many {name}",
  widgetFewer: "One fewer",
  widgetMore: "One more",
  widgetReturnRequested: "Return details sent",
  widgetExchangeRequested: "Exchange details sent",
  widgetBag: "Bag",
  widgetItemCount: "{n} items",
  widgetItemCountOne: "1 item",
  widgetQty: "Qty {n}",
  widgetSubtotal: "Subtotal",
  widgetDelivery: "Delivery",
  widgetTotal: "Total",
  widgetCheckout: "Checkout",
  widgetYourOffer: "Your offer",
  widgetEndsOn: "Ends {date}",
  widgetCopy: "Copy",
  widgetCopied: "Copied",
  widgetCopyCode: "Copy code {code}",
  widgetApplyToBag: "Apply to bag",
  widgetSimilarRequests: "Similar requests",
  widgetSimilarRequestsHint: "Upvote one to hear when it ships.",
  widgetUpvote: "Upvote",
  widgetUpvoteItem: "Upvote {name}",
  widgetVoted: "Voted",
  widgetVotes: "{n} votes",
  widgetVotesOne: "1 vote",
  widgetNoneOfThese: "None of these",
  widgetUpvotedLabel: "Upvoted: {name}",
  widgetUpvotedDetail: "Upvoted · we'll email you when it ships.",
  widgetNoneFit: "None of these fit",
  widgetNoneFitDetail: "Kai will write up a new request.",
  widgetNewRequest: "New request",
  widgetSubmitRequest: "Submit request",
  widgetSubmitRequestLabel: "Submit this request",
  widgetSubmitting: "Submitting…",
  widgetNotNow: "Not now",
  widgetRequestSubmitted: "Submitted",
  widgetRequestSubmittedDetail: "The team reviews new ideas.",
  widgetVoteRecorded: "Added to a request the team is reviewing",
  widgetVoteRecordedDetail: "We'll email you when it ships.",
  widgetRequestFailed: "That didn't go through. Please try again.",
  widgetEmail: "Your email",
  widgetEmailHint: "So we can tell you when it ships.",
  widgetEmailInvalid: "Please enter a valid email.",
  widgetChooseOption: "Choose an option",
  widgetRatingLow: "Not good",
  widgetRatingHigh: "Great",
  widgetDropFile: "Drop a file here",
  widgetDropFileHint: "Or choose one from your device.",
  widgetChooseFile: "Choose file",
  widgetChooseAnother: "Choose another",
  widgetUploading: "Uploading…",
  widgetVerifyTitle: "Confirm your email",
  widgetVerifyHint: "We'll send you a 6-digit code.",
  widgetSendCode: "Send code",
  widgetSendingCode: "Sending code",
  widgetEnterCode: "Enter your code",
  widgetCodeSentTo: "Sent to {email}",
  widgetResendCode: "Resend code",
  widgetResendIn: "Resend in {time}",
  widgetChangeEmail: "Change email",
  widgetWrongCode: "That code isn't right. {n} tries left.",
  widgetWrongCodeOne: "That code isn't right. 1 try left.",
  widgetCodeExpired: "That code has expired. Send a new one.",
  widgetEmailConfirmed: "Email confirmed",
  widgetEmailNotConfirmed: "Email not confirmed",
  widgetVerifyFailed: "Something went wrong. Please try again.",
};

export type ChatWidgetStringKey = keyof typeof CHAT_WIDGET_STRING_DEFAULTS;
export type ChatWidgetText = (
  key: ChatWidgetStringKey,
  params?: Record<string, string | number>,
) => string;

const fillParams = (
  value: string,
  params?: Record<string, string | number>,
): string =>
  params
    ? value.replace(/\{(\w+)\}/g, (match, name) =>
        params[name] !== undefined ? String(params[name]) : match,
      )
    : value;

/**
 * Card strings are project static translations like every other Messenger
 * string, with these English defaults until the dashboard ships the keys.
 * `{name}` placeholders are filled from `params`.
 */
export const useChatWidgetText = (): ChatWidgetText => {
  const staticTranslations = useConfigStore(
    (state) => state.config?.staticTranslations,
  );
  return (key, params) => {
    const value = staticTranslations?.[key];
    return fillParams(
      typeof value === "string" && value.length > 0
        ? value
        : CHAT_WIDGET_STRING_DEFAULTS[key],
      params,
    );
  };
};

/** A card whose answer runs a stored action on the server (billing, commerce). */
export const isServerRunCard = (widget: ChatWidgetData) =>
  widget.type === "confirm" &&
  (!!widget.billingActionId || !!widget.commerceActionId);

/**
 * How long a server-run card may show "pending" before the Messenger re-reads
 * the conversation: the server's answer can be lost (socket drop), or the
 * server can decide nothing ran. The re-read shows the real state, and an
 * unanswered card takes a new tap.
 */
const SERVER_RUN_CONFIRM_TIMEOUT_MS = 20000;

/**
 * Sends the customer's answer to Kai: marks the card answered right away (the
 * server confirms with `comment.updated`) and posts the answer as their
 * message, carrying the structured values in `widgetResponse`. When the
 * answer never reaches the server, the card goes back to how it was so the
 * customer can answer again.
 */
export const submitChatWidgetAnswer = async ({
  commentId,
  widget,
  action,
  values,
  label,
  mark = "answer",
}: {
  commentId: string;
  widget: ChatWidgetData;
  action: ChatWidgetAction;
  values: ChatWidgetValues;
  label: string;
  /**
   * How the card changes right away: `answer` sets the response, `undo`
   * stamps `undoneAt` on it (confirm Undo), `none` leaves it to the server
   * (a follow-up answer such as the Calendly confirmation).
   */
  mark?: "answer" | "undo" | "none";
}) => {
  const store = useTicketStore.getState();
  const previous = widget.response;
  const isPending = mark === "answer" && isServerRunCard(widget);
  let written: ChatWidgetResponse | undefined;
  if (mark === "answer") {
    written = store.markChatWidgetAnswered(commentId, {
      action,
      values,
      label,
      ...(isPending ? { pending: true } : {}),
    });
  }
  if (mark === "undo") {
    written = store.markChatWidgetUndone(commentId);
  }
  const result = await store
    .answerBotAction(
      {
        question: label,
        widgetResponse: { commentId, widgetId: widget.id, action, values },
      },
      [],
    )
    .catch(() => ({ sent: false as const }));
  if (!result.sent) {
    useTicketStore
      .getState()
      .restoreChatWidgetResponse(commentId, written, previous);
    return;
  }
  if (isPending && written) {
    setTimeout(() => {
      const comment = useTicketStore
        .getState()
        .currentComments.find((entry) => entry.id === commentId);
      if (comment?.data?.widget?.response !== written) return;
      useTicketStore
        .getState()
        .loadComments(true)
        .catch(() => {});
    }, SERVER_RUN_CONFIRM_TIMEOUT_MS);
  }
};
